import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Stack
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { approvalApi, type ApprovalRequest } from '../services/api';
import PageHeader from '../components/PageHeader';

const statusMap: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
  PENDING: { label: '待审批', color: 'warning' },
  APPROVED: { label: '已通过', color: 'success' },
  REJECTED: { label: '已驳回', color: 'error' },
};

const typeMap: Record<string, string> = {
  DOCUMENT_PUBLISH: '文档发布',
  ARTICLE_PUBLISH: '文章发布',
};

const ApprovalManagement: React.FC = () => {
  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [tab, setTab] = useState<'all' | 'pending'>('pending');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selected, setSelected] = useState<ApprovalRequest | null>(null);
  const [comment, setComment] = useState('');
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = tab === 'pending' ? await approvalApi.listPending() : await approvalApi.list();
      setItems(data);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || '加载审批列表失败', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleAction = async () => {
    if (!selected || !action) return;
    try {
      if (action === 'approve') {
        await approvalApi.approve(selected.id, comment);
      } else {
        await approvalApi.reject(selected.id, comment);
      }
      setSnackbar({ open: true, message: action === 'approve' ? '已通过审批' : '已驳回审批', severity: 'success' });
      setSelected(null);
      setAction(null);
      setComment('');
      load();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || '操作失败', severity: 'error' });
    }
  };

  const openDialog = (item: ApprovalRequest, act: 'approve' | 'reject') => {
    setSelected(item);
    setAction(act);
    setComment(item.comment || '');
  };

  return (
    <Box>
      <PageHeader title="审批管理" subtitle="文档与公众号文章发布审批" />

      <Paper sx={{ mb: 2, p: 1, borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary">
          <Tab value="pending" label="待审批" />
          <Tab value="all" label="全部记录" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>类型</TableCell>
              <TableCell>标题</TableCell>
              <TableCell>提交人</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>审批人</TableCell>
              <TableCell>审批意见</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '加载中...' : '暂无审批记录'}
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{typeMap[item.requestType] || item.requestType}</TableCell>
                  <TableCell>{item.requestTitle || `关联ID ${item.requestId}`}</TableCell>
                  <TableCell>{item.requester}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusMap[item.status]?.label || item.status}
                      color={statusMap[item.status]?.color || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.approver || '-'}</TableCell>
                  <TableCell>{item.comment || '-'}</TableCell>
                  <TableCell>{new Date(item.createdAt).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {item.status === 'PENDING' && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<ApproveIcon />}
                          onClick={() => openDialog(item, 'approve')}
                        >
                          通过
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => openDialog(item, 'reject')}
                        >
                          驳回
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{action === 'approve' ? '通过审批' : '驳回审批'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selected?.requestTitle || `请求 #${selected?.id}`}
          </Typography>
          <TextField
            label="审批意见（可选）"
            multiline
            rows={3}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>取消</Button>
          <Button variant="contained" color={action === 'approve' ? 'primary' : 'error'} onClick={handleAction}>
            {action === 'approve' ? '确认通过' : '确认驳回'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApprovalManagement;
